import { Component } from '@theme/component';
import { gsap } from '@theme/gsap';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Contact-hero animated form section.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [headline]
 * @property {HTMLElement} [ways]
 * @property {HTMLElement} [success]
 * @property {HTMLButtonElement} [submit]
 * @property {HTMLElement} [submitLabel]
 *
 * @extends {Component<Refs>}
 */
class ContactHero extends Component {
  #abort = new AbortController();
  #played = false;

  connectedCallback() {
    super.connectedCallback();

    // If the success card is in DOM, this is a post-submit reload.
    if (this.refs.success) {
      this.#animateSuccess();
      return;
    }

    if (this.#prefersReducedMotion()) {
      this.#wireSubmit();
      return;
    }

    this.#splitHeadline();
    this.#prepareInitialState();
    this.#observeAndPlay();
    this.#wireSubmit();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  #prefersReducedMotion() {
    return matchMedia(REDUCED_MOTION_QUERY).matches;
  }

  /**
   * Splits the headline text into per-word spans for staggered reveal.
   * Wraps each word in <span class="word"><span class="inner">word</span></span>
   * Whitespace is preserved as plain text nodes between word spans.
   */
  #splitHeadline() {
    const headline = this.refs.headline;
    if (!headline) return;

    const text = headline.textContent || '';
    const tokens = text.split(/(\s+)/);

    const fragment = document.createDocumentFragment();
    for (const token of tokens) {
      if (token === '') continue;
      if (/^\s+$/.test(token)) {
        fragment.appendChild(document.createTextNode(token));
        continue;
      }
      const wordOuter = document.createElement('span');
      wordOuter.className = 'contact-hero__word';
      const wordInner = document.createElement('span');
      wordInner.className = 'contact-hero__word-inner';
      wordInner.textContent = token;
      wordOuter.appendChild(wordInner);
      fragment.appendChild(wordOuter);
    }

    headline.replaceChildren(fragment);
    this.#words = headline.querySelectorAll('.contact-hero__word-inner');
  }

  /** @type {NodeListOf<HTMLElement>} */
  #words = /** @type {any} */ (null);

  /**
   * Hides headline words, ways block, and form fields so the timeline can
   * tween them in. Only runs when motion is allowed.
   */
  #prepareInitialState() {
    if (this.#words?.length) {
      gsap.set(this.#words, { yPercent: 110, opacity: 0 });
    }
    if (this.refs.ways) {
      gsap.set(this.refs.ways, { y: 20, opacity: 0 });
    }
    const fields = this.querySelectorAll('.contact-hero__field');
    if (fields.length) {
      gsap.set(fields, { y: 16, opacity: 0 });
    }
    if (this.refs.submit) {
      gsap.set(this.refs.submit, { y: 16, opacity: 0 });
    }
  }

  /**
   * Plays the entrance timeline once when the section enters the viewport,
   * or immediately if it's already on screen.
   */
  #observeAndPlay() {
    const play = () => {
      if (this.#played) return;
      this.#played = true;
      this.#playEntrance();
    };

    const rect = this.getBoundingClientRect();
    const inView = rect.top < window.innerHeight && rect.bottom > 0;
    if (inView) {
      play();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            play();
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(this);
    this.#abort.signal.addEventListener('abort', () => observer.disconnect());
  }

  #playEntrance() {
    const tl = gsap.timeline();
    if (this.#words?.length) {
      tl.to(this.#words, {
        yPercent: 0,
        opacity: 1,
        duration: 0.9,
        ease: 'power3.out',
        stagger: 0.06,
      });
    }
    if (this.refs.ways) {
      tl.to(
        this.refs.ways,
        { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' },
        '-=0.5'
      );
    }
    const fields = this.querySelectorAll('.contact-hero__field');
    if (fields.length) {
      tl.to(
        fields,
        { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out', stagger: 0.05 },
        '-=0.35'
      );
    }
    if (this.refs.submit) {
      tl.to(
        this.refs.submit,
        { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' },
        '-=0.2'
      );
    }
  }

  /**
   * Submit micro-interaction: button press-down + label fade.
   * Form submits normally (Shopify reload pattern).
   */
  #wireSubmit() {
    const form = this.querySelector('form');
    if (!form) return;
    const { signal } = this.#abort;

    form.addEventListener(
      'submit',
      () => {
        if (this.refs.submit) {
          gsap.fromTo(
            this.refs.submit,
            { scale: 1 },
            { scale: 0.97, duration: 0.1, yoyo: true, repeat: 1, ease: 'power2.inOut' }
          );
        }
        if (this.refs.submitLabel) {
          gsap.to(this.refs.submitLabel, {
            opacity: 0.4,
            duration: 0.15,
          });
        }
      },
      { signal }
    );
  }

  /**
   * Plays the success-card entrance after a successful submit reload.
   * Safe to call when prefers-reduced-motion is on — uses gsap.from which
   * sets final state at end, so reduced-motion still ends in correct DOM.
   */
  #animateSuccess() {
    if (this.#prefersReducedMotion()) return;
    if (!this.refs.success) return;
    gsap.from(this.refs.success, {
      scale: 0.95,
      opacity: 0,
      duration: 0.6,
      ease: 'power3.out',
    });
  }
}

if (!customElements.get('contact-hero')) {
  customElements.define('contact-hero', ContactHero);
}
